/**
 * AIP-160 Filter Builder for TypeScript
 * Builds Google AIP-160 compliant filter strings
 */

export type Primitive = string | number | boolean;
export type FilterValue = Primitive | Primitive[];

export interface FilterOptions {
  /**
   * Whether to wrap string values in quotes. Defaults to true.
   */
  quotes?: boolean;
  /**
   * Whether to add spaces around operators. Defaults to true.
   */
  space?: boolean;
}

class FilterBuilder {
  private conditions: string[] = [];

  /**
   * Add an equality condition
   */
  equals(field: string, value: Primitive, options?: FilterOptions): this {
    const space = options?.space ?? true;
    const operator = space ? ' = ' : '=';
    this.conditions.push(
      `${field}${operator}${this.formatValue(value, options?.quotes ?? true)}`,
    );
    return this;
  }

  /**
   * Add a not-equals condition
   */
  notEquals(field: string, value: Primitive, options?: FilterOptions): this {
    const space = options?.space ?? true;
    const operator = space ? ' != ' : '!=';
    this.conditions.push(
      `${field}${operator}${this.formatValue(value, options?.quotes ?? true)}`,
    );
    return this;
  }

  /**
   * Add a greater-than condition
   */
  greaterThan(
    field: string,
    value: number | string,
    options?: FilterOptions,
  ): this {
    const space = options?.space ?? true;
    const operator = space ? ' > ' : '>';
    const formattedValue =
      typeof value === 'string'
        ? this.formatValue(value, options?.quotes ?? true)
        : value;
    this.conditions.push(`${field}${operator}${formattedValue}`);
    return this;
  }

  /**
   * Add a greater-than-or-equal condition
   */
  greaterThanOrEqual(
    field: string,
    value: number | string,
    options?: FilterOptions,
  ): this {
    const space = options?.space ?? true;
    const operator = space ? ' >= ' : '>=';
    const formattedValue =
      typeof value === 'string'
        ? this.formatValue(value, options?.quotes ?? true)
        : value;
    this.conditions.push(`${field}${operator}${formattedValue}`);
    return this;
  }

  /**
   * Add a less-than condition
   */
  lessThan(
    field: string,
    value: number | string,
    options?: FilterOptions,
  ): this {
    const space = options?.space ?? true;
    const operator = space ? ' < ' : '<';
    const formattedValue =
      typeof value === 'string'
        ? this.formatValue(value, options?.quotes ?? true)
        : value;
    this.conditions.push(`${field}${operator}${formattedValue}`);
    return this;
  }

  /**
   * Add a less-than-or-equal condition
   */
  lessThanOrEqual(
    field: string,
    value: number | string,
    options?: FilterOptions,
  ): this {
    const space = options?.space ?? true;
    const operator = space ? ' <= ' : '<=';
    const formattedValue =
      typeof value === 'string'
        ? this.formatValue(value, options?.quotes ?? true)
        : value;
    this.conditions.push(`${field}${operator}${formattedValue}`);
    return this;
  }

  /**
   * Add a 'has' condition (checks for field presence or contains)
   */
  has(field: string, value: Primitive, options?: FilterOptions): this {
    // 'has' operator uses ':' which doesn't have spaces
    this.conditions.push(
      `${field}:${this.formatValue(value, options?.quotes ?? true)}`,
    );
    return this;
  }

  /**
   * Add a wildcard match condition
   */
  matches(field: string, pattern: string, options?: FilterOptions): this {
    const space = options?.space ?? true;
    const operator = space ? ' = ' : '=';
    this.conditions.push(
      `${field}${operator}${this.formatValue(pattern, options?.quotes ?? true)}`,
    );
    return this;
  }

  /**
   * Add a raw condition string
   */
  raw(condition: string): this {
    this.conditions.push(condition);
    return this;
  }

  /**
   * Combine multiple conditions with AND
   */
  and(...builders: FilterBuilder[]): this {
    const combined = builders
      .map(b => {
        const built = b.build();
        return built.includes(' OR ') ? `(${built})` : built;
      })
      .filter(Boolean)
      .join(' AND ');

    if (combined) {
      this.conditions.push(combined);
    }
    return this;
  }

  /**
   * Combine multiple conditions with OR
   */
  or(...builders: FilterBuilder[]): this {
    const combined = builders
      .map(b => `(${b.build()})`)
      .filter(Boolean)
      .join(' OR ');

    if (combined) {
      this.conditions.push(combined);
    }
    return this;
  }

  /**
   * Add a NOT condition
   */
  not(builder: FilterBuilder): this {
    const built = builder.build();
    this.conditions.push(`NOT (${built})`);
    return this;
  }

  /**
   * Build the final filter string
   */
  build(): string {
    // Only wrap OR conditions if there are multiple conditions (OR combined with AND)
    const hasMultipleConditions = this.conditions.length > 1;
    const hasNonOrConditions = this.conditions.some(c => !c.includes(' OR '));

    return this.conditions
      .map(condition => {
        // Wrap OR conditions in parentheses when combined with AND conditions
        if (
          condition.includes(' OR ') &&
          hasMultipleConditions &&
          hasNonOrConditions
        ) {
          const trimmed = condition.trim();
          // Check if the entire condition is already wrapped in balanced parentheses
          if (
            trimmed.startsWith('(') &&
            trimmed.endsWith(')') &&
            this.isFullyWrapped(trimmed)
          ) {
            return condition;
          }
          return `(${condition})`;
        }
        return condition;
      })
      .join(' AND ');
  }

  /**
   * Check if a string is fully wrapped in balanced parentheses
   */
  private isFullyWrapped(str: string): boolean {
    if (!str.startsWith('(') || !str.endsWith(')')) {
      return false;
    }
    let depth = 0;
    for (let i = 0; i < str.length; i++) {
      if (str[i] === '(') {
        depth++;
      } else if (str[i] === ')') {
        depth--;
        // If we reach depth 0 before the end, it's not fully wrapped
        if (depth === 0 && i < str.length - 1) {
          return false;
        }
      }
    }
    return depth === 0;
  }

  /**
   * Format a value according to AIP-160 rules
   */
  private formatValue(value: Primitive, quotes: boolean = true): string {
    if (typeof value === 'string') {
      if (quotes) {
        // Escape backslashes first, then escape quotes and wrap in quotes
        return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
      }
      // Return string without quotes
      return value;
    }
    if (typeof value === 'boolean') {
      return value.toString();
    }
    return value.toString();
  }

  /**
   * Create a new filter builder
   */
  static create(): FilterBuilder {
    return new FilterBuilder();
  }
}

export { FilterBuilder };
